/*
 * 模块: MetricCard
 * 职责: 组合所有子组件，渲染完整的指标卡片
 * 依赖: antd组件、类型定义、子组件
 * 导出: MetricCard组件
 * 限制: 文件<280行, 函数<30行
 */

import React from 'react';
import { Card, Tabs, Divider, Alert, Space, Tag, Typography } from 'antd';
import {
  InfoCircleOutlined,
  CalculatorOutlined,
  QuestionCircleOutlined,
  ExperimentOutlined,
} from '@ant-design/icons';
import type { MetricDefinition } from '../../../types';
import { MetricHeader } from './MetricHeader';
import { OverviewTab } from './OverviewTab';
import { CalculationTab } from './CalculationTab';
import { TermsTab } from './TermsTab';
import { CaseTab } from './CaseTab';

const { Text } = Typography;

interface MetricCardProps {
  metricKey: string;
  definition: MetricDefinition;
}

const buildTabItems = (definition: MetricDefinition) => {
  return [
    {
      key: 'overview',
      label: (
        <span>
          <InfoCircleOutlined style={{ marginRight: 6 }} />
          概览
        </span>
      ),
      children: <OverviewTab definition={definition} />
    },
    {
      key: 'calculation',
      label: (
        <span>
          <CalculatorOutlined style={{ marginRight: 6 }} />
          详细计算
        </span>
      ),
      children: <CalculationTab definition={definition} />
    },
    {
      key: 'terms',
      label: (
        <span>
          <QuestionCircleOutlined style={{ marginRight: 6 }} />
          术语解释
        </span>
      ),
      children: <TermsTab definition={definition} />
    },
    {
      key: 'case',
      label: (
        <span>
          <ExperimentOutlined style={{ marginRight: 6 }} />
          实战案例
        </span>
      ),
      children: <CaseTab definition={definition} />
    }
  ];
};

const renderWeightInfo = (weights: string[]) => {
  if (!weights || weights.length === 0) return null;

  return (
    <>
      <Divider />
      <Alert
        message="权重影响"
        description={
          <Space>
            <Text>该指标受以下权重参数影响：</Text>
            {weights.map((weight) => (
              <Tag key={weight} color="purple">{weight}</Tag>
            ))}
          </Space>
        }
        type="info"
        showIcon
      />
    </>
  );
};

export const MetricCard: React.FC<MetricCardProps> = ({ metricKey, definition }) => {
  const tabItems = buildTabItems(definition);

  return (
    <Card key={metricKey} id={metricKey} className="metric-detail-card" style={{ marginBottom: 32 }}>
      <MetricHeader definition={definition} />
      
      <Tabs 
        defaultActiveKey="overview" 
        items={tabItems}
        size="large"
      />

      {renderWeightInfo(definition.affected_by_weights)}
    </Card>
  );
};
